"use client";
import FlagIcon from '@mui/icons-material/Flag';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { useState } from "react";
import type { AnonymousLyricsReportRequest } from '~/connectors/lyricsReports.connector';
import { anonymousLyricsReportsService } from '~/connectors/lyricsReports.connector';
import type { LyricsReportCategory } from '~/types';

type CategoryMeta = { label: string; placeholder: string };

const CATEGORIES: Record<LyricsReportCategory, CategoryMeta> = {
  // Erori de conținut
  greseala_text: {
    label: 'Greșeli de scriere (typo)',
    placeholder: 'Ex: „La versul 3 lipsește un cuvânt”',
  },
  versuri_incomplete: {
    label: 'Lipsesc versuri / strofe',
    placeholder: 'Ex: Indică versul sau strofa care lipsește',
  },
  videoclip_gresit: {
    label: 'Videoclipul nu corespunde',
    placeholder: 'Ex: Specifică ce videoclip este afișat și de ce nu e corect',
  },
  text_incorect: {
    label: 'Versurile sunt de la alt cântec',
    placeholder: 'Ex: Specifică care parte pare nepotrivită pentru acest cântec',
  },

  // Erori de formă
  formatare_urata: {
    label: 'Textul nu este așezat corect',
    placeholder: 'Ex: Probleme de aliniere sau spațiere între versuri',
  },
  punctuatie: {
    label: 'Lipsesc semne de punctuație',
    placeholder: 'Ex: Adaugă locul unde lipsește o virgulă sau punct',
  },

  // Siguranță și Calitate
  limbaj_inadecvat: {
    label: 'Conținut nepotrivit / ofensator',
    placeholder: 'Ex: Indică expresia sau versul ofensator',
  },
  drepturi_autor: {
    label: 'Încălcare drepturi de autor (Copyright)',
    placeholder: 'Te rugăm să indici sursa originală sau motivul eliminării acestui conținut',
  },
  alta_problema: {
    label: 'Altceva (detaliază mai jos)',
    placeholder: 'Ex: Descrie problema în câteva cuvinte',
  },
};
const CATEGORY_OPTIONS = Object.entries(CATEGORIES) as [LyricsReportCategory, CategoryMeta][];
const DEFAULT_CATEGORY: LyricsReportCategory = 'greseala_text';

export default function ReportButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<LyricsReportCategory>(DEFAULT_CATEGORY);
  const [description, setDescription] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' } | null>(null);
  const cooldownKey = 'last_lyrics_report_ts';

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    try {
      const last = Number(window.sessionStorage.getItem(cooldownKey) || 0);
      const now = Date.now();
      const cooldownMs = 8000; // 8s client-side guard
      if (last && now - last < cooldownMs) {
        setSnack({ open: true, message: 'Ai trimis prea recent. Așteaptă puțin.', severity: 'error' });
        return;
      }

      const payload: AnonymousLyricsReportRequest = {
        url: window.location.href,
        category,
        description: description || undefined,
      };

      await anonymousLyricsReportsService.create(payload);

      setSnack({ open: true, message: 'Mulțumim! Ești un ajutor de nădejde. 🎵', severity: 'success' });
      window.sessionStorage.setItem(cooldownKey, String(Date.now()));
      setOpen(false);
      setDescription('');
      setCategory(DEFAULT_CATEGORY);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Eroare de rețea';
      setSnack({ open: true, message, severity: 'error' });
    }
  };

  const descriptionPlaceholder = CATEGORIES[category]?.placeholder || 'Descrie problema...';

  return (
    <>
      <Button
        aria-label="Spune-ne dacă ceva nu e în regulă"
        size="small"
        startIcon={<FlagIcon fontSize="small" />}
        onClick={handleOpen}
        sx={{ textTransform: 'none', fontSize: "0.875rem", opacity: 0.7, '&:hover': { opacity: 1 } }}
        color="neutral"
      >
        Ai găsit o greșeală?
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>Ajută-ne să corectăm versurile</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="report-category-label">Categorie</InputLabel>
            <Select
              labelId="report-category-label"
              value={category}
              label="Categorie"
              onChange={(e) => setCategory(e.target.value as LyricsReportCategory)}
            >
              {CATEGORY_OPTIONS.map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Anulează</Button>
          <Button onClick={handleSubmit} variant="contained">Trimite Raportul</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack?.open} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity ?? 'success'} sx={{ width: '100%' }}>
          {snack?.message ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
}
